import { base64ToBlob } from '@/utils/base64ToBlob';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { tetriFromDecimal } from '@/utils/money';
import { BranchesEndpoints } from './branchesEndpoints';
import type { IBranchesService } from './IBranchesService';
import type {
    BranchCreateRequest,
    BranchListQuery,
    BranchWriteRequest,
} from './requests';
import type {
    BranchWriteResponse,
    GenerateBranchApiTokenResponse,
    GetBranchApiTokenResponse,
    GetBranchDetailsResponse,
    GetBranchListResponse,
} from './responses';

type BranchesServiceOptions = {
    baseUrl: string;
};

export class BranchesService implements IBranchesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: BranchesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public getBranchList(): Promise<ApiResponse<GetBranchListResponse>> {
        const url = BranchesEndpoints.list();
        return makeApiRequest<GetBranchListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public async getBranchDetails(
        query: BranchListQuery,
    ): Promise<ApiResponse<GetBranchDetailsResponse>> {
        const url = BranchesEndpoints.details();
        // The bos-api binds these case-insensitively; `search` is omitted when
        // blank so an empty box returns the unfiltered page.
        const params: Record<string, string | number | boolean> = {
            page: query.page,
            pageSize: query.pageSize,
        };
        const search = query.search?.trim();
        if (search) {
            params.search = search;
        }
        if (query.includeArchived) {
            params.includeArchived = true;
        }
        const result = await makeApiRequest<GetBranchDetailsResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, { params }),
        );
        return result.success
            ? {
                  success: true,
                  response: {
                      ...result.response,
                      // BOS-162: the one edge where the branch's inventory
                      // value crosses into integer tetri.
                      items: (result.response.items ?? []).map((item) => ({
                          ...item,
                          inventoryValueTetri: tetriFromDecimal(
                              item.inventoryValue,
                          ),
                      })),
                  },
              }
            : result;
    }

    public createBranch(
        body: BranchCreateRequest,
    ): Promise<ApiResponse<BranchWriteResponse>> {
        const url = BranchesEndpoints.create();

        // bos-api binds [FromForm] on POST /branches — name + address +
        // isMain (required), optional `area` and `file`. Packing as multipart
        // lets the server validate the image and persist the branch in one
        // round-trip.
        const formData = new FormData();
        formData.append('name', body.name);
        formData.append('address', body.address);
        // `isMain` is ALWAYS sent explicitly — omitting it returns
        // `400 Field 'isMain' is required.`
        formData.append('isMain', body.isMain ? 'true' : 'false');
        if (body.area !== null) {
            formData.append('area', String(body.area));
        }
        if (body.imageUpload) {
            formData.append(
                'file',
                base64ToBlob(
                    body.imageUpload.dataBase64,
                    body.imageUpload.mimeType,
                ),
                body.imageUpload.fileName,
            );
        }

        return makeApiRequest<BranchWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, formData),
        );
    }

    public updateBranch(
        id: string,
        body: BranchWriteRequest,
    ): Promise<ApiResponse<BranchWriteResponse>> {
        const url = BranchesEndpoints.update(id);
        return makeApiRequest<BranchWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }

    public setBranchArchived(
        id: string,
        archived: boolean,
    ): Promise<ApiResponse<unknown>> {
        // Two routes rather than a PATCH with a flag: archiving and
        // unarchiving are separate acts server-side, and only one of them can
        // be refused (the main branch cannot be archived).
        const url = archived
            ? BranchesEndpoints.archive(id)
            : BranchesEndpoints.unarchive(id);
        return makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }

    public deleteBranch(id: string): Promise<ApiResponse<void>> {
        const url = BranchesEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public generateApiToken(
        id: string,
    ): Promise<ApiResponse<GenerateBranchApiTokenResponse>> {
        // POST mints a fresh token and revokes any previous one (rotate). The
        // plaintext `token` comes back in the response and is also persisted
        // on the row (BOS-77/79), so the same value can be re-fetched later
        // via `getApiToken`.
        const url = BranchesEndpoints.apiToken(id);
        return makeApiRequest<GenerateBranchApiTokenResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }

    public getApiToken(
        id: string,
    ): Promise<ApiResponse<GetBranchApiTokenResponse>> {
        // BOS-77/79: re-read the active branch API token. `token` is null
        // on legacy hash-only rows (pre-BOS-77 issue) and null + setAt null
        // when no token has been issued (or it was revoked).
        const url = BranchesEndpoints.apiToken(id);
        return makeApiRequest<GetBranchApiTokenResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public revealApiToken(
        id: string,
    ): Promise<ApiResponse<GenerateBranchApiTokenResponse>> {
        // BOS-209: POST (not GET) so the secret stays out of caches and URLs.
        // 404 when the token can't be recovered (none active, or issued
        // before the server kept an encrypted copy) — rotate to get one.
        const url = BranchesEndpoints.revealApiToken(id);
        return makeApiRequest<GenerateBranchApiTokenResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }

    public revokeApiToken(id: string): Promise<ApiResponse<void>> {
        const url = BranchesEndpoints.apiToken(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }
}
